/**
 * System Status Banner
 *
 * Shows a non-alarming notice at the top of the app when a third-party provider
 * Kantera depends on (Remotion, Replicate, Stripe, Postmark, …) is having trouble,
 * so a partner's outage doesn't make Kantera look broken. Backed by the real
 * dependency-health kill-switch (`GET /api/system/status`) — the banner is driven
 * by actual circuit-breaker state, never a synthetic flag (Law 9).
 *
 * Silent by default: renders nothing while healthy, loading, or if the status
 * endpoint itself errors — a status widget must never nag or scare on its own.
 * Optionally scope to a single feature via `feature` so a page only warns about
 * providers that actually affect it.
 *
 * Copy is translated client-side by provider slug (`systemStatus.providers.<slug>`
 * in packages/i18n). The API's own English `message` is only a fallback for a
 * slug the locale files don't know yet, and the generic line covers the case
 * where the API sent no message at all.
 *
 * It also speaks up when THIS page was rate-limited (KAN-139). The API limits
 * each user per minute and the client never retries a 429, so a page that fans
 * out one request per row loses some of them and renders holes with no reason
 * given. @kantera/api-client fires RATE_LIMITED_EVENT on window for the
 * limiter's 429 only (body `code: 'RATE_LIMIT_EXCEEDED'` — not the business-quota
 * 429s, which reloading cannot fix); this turns it into one sentence, a countdown
 * when the API said how long, and a Reload button. It clears when the user moves
 * to another page.
 */
import { type CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, theme } from '@kantera/ui';
import type { SystemStatusResponse } from '@kantera/types';
import { RATE_LIMITED_EVENT, type RateLimitedEventDetail } from '@kantera/api-client';
import { useApi } from '../hooks/useApi';

interface SystemStatusBannerProps {
  /** When set, only show degradations whose affectedFeatures include this exact label. */
  feature?: string;
}

const POLL_MS = 60_000;

const WRAP_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing.sm,
  margin: `${theme.spacing.md} 0`,
  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
  background: theme.colors.bg.secondary,
  border: `1px solid ${theme.colors.warning.primary}`,
  borderLeft: `3px solid ${theme.colors.warning.primary}`,
  borderRadius: theme.radius.md,
  color: theme.colors.text.primary,
  fontSize: theme.typography.fontSize.sm.size,
  lineHeight: theme.typography.fontSize.sm.lineHeight,
};

const DOT_STYLE: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: theme.radius.full,
  background: theme.colors.warning.primary,
  marginTop: 4,
  flex: 'none',
};

const LIST_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.xs,
};

const MSG_STYLE: CSSProperties = {
  color: theme.colors.text.secondary,
};

const RATE_LIMIT_WRAP_STYLE: CSSProperties = {
  ...WRAP_STYLE,
  alignItems: 'center',
};

const RATE_LIMIT_TEXT_STYLE: CSSProperties = {
  flex: 1,
};

/**
 * Whether a request on the current page was rate-limited, and how many seconds
 * are left before the LATEST limit resets (null when the API did not say, or
 * once it has passed). Many requests are refused together — one per row — and
 * the page can be whole again only after the last of them resets.
 */
function useRateLimited(): { limited: boolean; secondsLeft: number | null } {
  const { pathname } = useLocation();
  const [limited, setLimited] = useState(false);
  const [resetAt, setResetAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const onRateLimited = (event: Event) => {
      const detail = (event as CustomEvent<Partial<RateLimitedEventDetail> | null>).detail;
      const retryAfter = detail?.retryAfter;
      const at = Date.now();
      setLimited(true);
      setNow(at);
      if (typeof retryAfter === 'number' && Number.isFinite(retryAfter) && retryAfter >= 0) {
        const next = at + retryAfter * 1000;
        setResetAt((prev) => (prev !== null && prev > next ? prev : next));
      }
    };
    window.addEventListener(RATE_LIMITED_EVENT, onRateLimited);
    return () => window.removeEventListener(RATE_LIMITED_EVENT, onRateLimited);
  }, []);

  // The notice describes the page it happened on; a new page starts clean.
  useEffect(() => {
    setLimited(false);
    setResetAt(null);
  }, [pathname]);

  useEffect(() => {
    if (resetAt === null) return;
    const id = setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (tick >= resetAt) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [resetAt]);

  const left = resetAt === null ? 0 : Math.ceil((resetAt - now) / 1000);
  return { limited, secondsLeft: left > 0 ? left : null };
}

export function SystemStatusBanner({ feature }: SystemStatusBannerProps) {
  const { t } = useTranslation();
  const { data, refetch } = useApi<SystemStatusResponse>('/api/system/status');
  const rateLimited = useRateLimited();

  useEffect(() => {
    const id = setInterval(() => {
      void refetch();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [refetch]);

  const status = data?.data;
  const degraded =
    status?.status === 'degraded'
      ? status.providers.filter(
          (p) => p.health === 'degraded' && (!feature || p.affectedFeatures.includes(feature)),
        )
      : [];
  if (!rateLimited.limited && degraded.length === 0) return null;

  const rateLimitedText =
    rateLimited.secondsLeft === null
      ? t('systemStatus.rateLimited.message')
      : `${t('systemStatus.rateLimited.message')} ${t('systemStatus.rateLimited.retryIn', {
          seconds: rateLimited.secondsLeft,
        })}`;

  return (
    <>
      {rateLimited.limited && (
        <div role="status" aria-live="polite" style={RATE_LIMIT_WRAP_STYLE}>
          <span style={DOT_STYLE} aria-hidden />
          <span style={RATE_LIMIT_TEXT_STYLE}>{rateLimitedText}</span>
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
            {t('systemStatus.rateLimited.reload')}
          </Button>
        </div>
      )}
      {degraded.length > 0 && (
        <div role="status" aria-live="polite" style={WRAP_STYLE}>
          <span style={DOT_STYLE} aria-hidden />
          <div style={LIST_STYLE}>
            <strong>{t('systemStatus.title')}</strong>
            {degraded.map((p) => (
              <span key={p.slug} style={MSG_STYLE}>
                {t(`systemStatus.providers.${p.slug}`, {
                  defaultValue: p.message ?? t('systemStatus.generic', { vendor: p.vendor }),
                })}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
